import { nonUndefined, onlyUnique } from "Utility";
import { RosterScope, DRIVER_SCOPE } from "./RosterScope";

export const BYE = "BYE";
export const TBD = "TBD";
export type PlaceholderRoster = typeof BYE | typeof TBD;

export type RosterBase = {
  disqualified: boolean | undefined;
  forfeited: boolean | undefined;
};

export type DriverRoster = RosterBase & {
  driverId: string;
};

export type SquadRoster = RosterBase & {
  driverIds: string[] | undefined;
  teamId: string;
};

export type Roster = string | DriverRoster | SquadRoster;

export const isDriverRoster = (roster: Roster): roster is DriverRoster =>
  typeof roster === "object" && !!(roster as DriverRoster).driverId;

export const isSquadRoster = (roster: Roster): roster is SquadRoster =>
  typeof roster === "object" && !!(roster as SquadRoster).teamId;

export const isPlaceholderRoster = (
  roster: Roster
): roster is PlaceholderRoster => roster === TBD || roster === BYE;

export const isPlaceholderRosterId = (rosterId: string) =>
  rosterId === TBD || rosterId === BYE;

export const getDriverRosterId = (driver: DriverRoster) => driver.driverId;
export const getSquadRosterId = (squad: SquadRoster) => squad.teamId;

export const getRosterId = (roster: Roster) => {
  if (isPlaceholderRoster(roster)) {
    return undefined;
  }

  if (typeof roster === "string") {
    return roster;
  }

  return isDriverRoster(roster)
    ? getDriverRosterId(roster)
    : getSquadRosterId(roster);
};

export const getRosterIdOrPlaceholder = (roster: Roster) => {
  if (typeof roster === "string") {
    return roster;
  }

  return isDriverRoster(roster)
    ? getDriverRosterId(roster)
    : getSquadRosterId(roster);
};

export const getRosterFromDriverId = (
  driverId: string,
  rosters: Roster[],
  scope: RosterScope
) => {
  for (let i = 0; i < rosters.length; i++) {
    const roster = rosters[i];
    const rosterDriverIds = getRosterDriverIds(roster, scope);
    if (!!rosterDriverIds && rosterDriverIds.includes(driverId)) {
      return roster;
    }
  }

  return undefined;
};

export const getRosterIdFromDriverId = (
  driverId: string,
  rosters: Roster[],
  scope: RosterScope
) => {
  const roster = getRosterFromDriverId(driverId, rosters, scope);
  return !!roster ? getRosterId(roster) : undefined;
};

export const getDefinedRosters = (rosters: Roster[]) =>
  rosters.filter((r) => !isPlaceholderRoster(r));

export const getDefinedRosterIds = (rosters: Roster[]) =>
  nonUndefined(rosters.flatMap(getRosterId));

export const getRoster = (rosters: Roster[], rosterId: string) =>
  getDefinedRosters(rosters).find((r) => getRosterId(r) === rosterId);

export const getRosterDriverIds = (roster: Roster, scope: RosterScope) => {
  if (isPlaceholderRoster(roster)) {
    return undefined;
  }

  if (scope === DRIVER_SCOPE) {
    if (typeof roster === "string") {
      return [roster];
    }

    return isDriverRoster(roster) ? [roster.driverId] : undefined;
  }

  return isSquadRoster(roster) ? roster.driverIds : undefined;
};

export const getDriverIdsFromRosterIds = (
  rosters: Roster[],
  scope: RosterScope,
  rosterIds: string[]
) => {
  return rosterIds
    .flatMap((id) => {
      const roster = getRoster(rosters, id);
      if (!roster) {
        return [];
      }

      return getRosterDriverIds(roster, scope) ?? [];
    })
    .filter(onlyUnique);
};

export const isDisqualified = (roster: Roster) =>
  typeof roster !== "string" && roster.disqualified === true;

export const isForfeited = (roster: Roster) =>
  typeof roster !== "string" && roster.forfeited === true;

export const getDisqualifiedRosterIds = (rosters: Roster[]) =>
  nonUndefined(rosters.filter(isDisqualified).flatMap(getRosterId));

export const getForfeitedRosterIds = (rosters: Roster[]) =>
  nonUndefined(rosters.filter(isForfeited).flatMap(getRosterId));

export const getNonForfeitedRosterIds = (rosters: Roster[]) =>
  nonUndefined(rosters.filter((r) => !isForfeited(r)).flatMap(getRosterId));

export const getNumPlayingRosters = (
  rosters: Roster[],
  includeForfeits: boolean = true
) =>
  (rosters as Roster[]).filter(
    (r) => r !== BYE && (includeForfeits || !isForfeited(r))
  ).length;
